import { operatorRolesApi, type BusinessGroupRoleEntity } from '@data-delivery/permission-ui-api';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { permissionQueryClient as qc } from '../query-client';

const ROLES_SESSION_KEY = 'permission-ui:user-roles';
const ROLES_TIMESTAMP_KEY = 'permission-ui:user-roles-timestamp';
const CACHE_DURATION_MS = 30 * 60 * 1000;
const REFETCH_INTERVAL_MS = 30 * 60 * 1000;

export const currentUserRolesKeys = {
  all: ['current-user-roles'] as const,
  roles: () => [...currentUserRolesKeys.all, 'roles'] as const,
};

const rolesCache = {
  get(): BusinessGroupRoleEntity[] | undefined {
    try {
      const stored = sessionStorage.getItem(ROLES_SESSION_KEY);
      const timestamp = sessionStorage.getItem(ROLES_TIMESTAMP_KEY);

      if (!stored || !timestamp) {
        return undefined;
      }

      const age = Date.now() - parseInt(timestamp, 10);
      if (age > CACHE_DURATION_MS) {
        this.clear();
        return undefined;
      }

      return JSON.parse(stored) as BusinessGroupRoleEntity[];
    } catch {
      return undefined;
    }
  },

  set(roles: BusinessGroupRoleEntity[]): void {
    try {
      sessionStorage.setItem(ROLES_SESSION_KEY, JSON.stringify(roles));
      sessionStorage.setItem(ROLES_TIMESTAMP_KEY, Date.now().toString());
    } catch {}
  },

  clear(): void {
    try {
      sessionStorage.removeItem(ROLES_SESSION_KEY);
      sessionStorage.removeItem(ROLES_TIMESTAMP_KEY);
    } catch {}
  },
};

export function useCurrentUserRoles(
  options?: Omit<
    UseQueryOptions<
      BusinessGroupRoleEntity[],
      unknown,
      BusinessGroupRoleEntity[],
      readonly ['current-user-roles', 'roles']
    >,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery({
    queryKey: currentUserRolesKeys.roles(),
    async queryFn() {
      const { data } = await operatorRolesApi.getRolesByUserId();
      const roles = data ?? [];

      rolesCache.set(roles);

      return roles;
    },
    staleTime: CACHE_DURATION_MS,
    gcTime: Infinity,
    refetchInterval: REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: false,
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 3000),

    initialData() {
      return rolesCache.get();
    },
    initialDataUpdatedAt() {
      const timestamp = sessionStorage.getItem(ROLES_TIMESTAMP_KEY);
      return timestamp ? parseInt(timestamp, 10) : 0;
    },
    ...options,
  });
}

export async function prefetchCurrentUserRoles(): Promise<void> {
  const cached = rolesCache.get();
  if (cached) {
    qc.setQueryData(currentUserRolesKeys.roles(), cached);

    qc.prefetchQuery({
      queryKey: currentUserRolesKeys.roles(),
      async queryFn() {
        const { data } = await operatorRolesApi.getRolesByUserId();
        const roles = data ?? [];
        rolesCache.set(roles);
        return roles;
      },
      staleTime: CACHE_DURATION_MS,
    });
    return;
  }

  await qc.prefetchQuery({
    queryKey: currentUserRolesKeys.roles(),
    async queryFn() {
      const { data } = await operatorRolesApi.getRolesByUserId();
      const roles = data ?? [];
      rolesCache.set(roles);
      return roles;
    },
    staleTime: CACHE_DURATION_MS,
  });
}

export function invalidateCurrentUserRoles(): Promise<void> {
  rolesCache.clear();
  return qc.invalidateQueries({ queryKey: currentUserRolesKeys.all });
}

export function getCachedCurrentUserRoles(): BusinessGroupRoleEntity[] | undefined {
  const queryData = qc.getQueryData<BusinessGroupRoleEntity[]>(currentUserRolesKeys.roles());
  if (queryData) {
    return queryData;
  }

  return rolesCache.get();
}

export function clearCurrentUserRoles(): void {
  rolesCache.clear();
  qc.removeQueries({ queryKey: currentUserRolesKeys.all });
}
